import type { MembershipBase } from 'sdk';
import type { ChannelEntityType, EntityActionType, EntityCanMap, EntityType } from 'shared';
import { allActionsAllowed, appConfig, computeCan, hierarchy, policyMatrix } from 'shared';
import { asRecord } from 'shared/utils/as-record';
import { useUserStore } from '~/modules/user/user-store';
import type { EnrichableChannel } from '~/query/enrichment/types';

/** Deep compare over the can states (booleans and condition names). */
function hasCanChanged(a: EntityCanMap | undefined, b: EntityCanMap | undefined): boolean {
  if (!a && !b) return false;
  if (!a || !b) return true;

  const aKeys = Object.keys(a) as EntityType[];
  const bKeys = Object.keys(b) as EntityType[];
  if (aKeys.length !== bKeys.length) return true;

  return aKeys.some((key) => {
    const aPerms = a[key];
    const bPerms = b[key];
    if (!aPerms && !bPerms) return false;
    if (!aPerms || !bPerms) return true;
    return (Object.keys(aPerms) as EntityActionType[]).some((action) => aPerms[action] !== bPerms[action]);
  });
}

/** Mirrors the shape of computeCan over the channel type and its descendants, with every action allowed. */
function computeSystemAdminCan(channelType: ChannelEntityType): EntityCanMap {
  const map: EntityCanMap = { [channelType]: { ...allActionsAllowed } };
  for (const descendant of hierarchy.getOrderedDescendants(channelType)) {
    map[descendant] = { ...allActionsAllowed };
  }
  return map;
}

/** The actor's memberships on the channels above the row, found by the ancestor ids the row carries. */
function membershipsAbove(item: EnrichableChannel, channelType: ChannelEntityType, memberships: MembershipBase[]): MembershipBase[] {
  const row = asRecord(item);
  const ancestorIds = new Set(hierarchy.getOrderedAncestors(channelType).map((ancestor) => row[appConfig.entityIdColumnKeys[ancestor]]));
  return memberships.filter((membership) => membership.channelId !== item.id && ancestorIds.has(membership.channelId));
}

/**
 * Permission map keyed by entity type, self plus hierarchy descendants, computed as the backend decides: from the actor's
 * membership on the row and their memberships on the channels above it. A system admin gets every action, with or without a membership.
 */
export function enrichWithPermissions(
  item: EnrichableChannel,
  channelType: ChannelEntityType,
  memberships: MembershipBase[] = [],
): EnrichableChannel {
  const existing = item.can;

  const grants = [...(item.membership ? [item.membership] : []), ...membershipsAbove(item, channelType, memberships)];
  const computed = useUserStore.getState().isSystemAdmin ? computeSystemAdminCan(channelType) : computeCan(channelType, grants, policyMatrix);

  if (!hasCanChanged(existing, computed)) return item;

  return { ...item, can: computed };
}
